import {
    XyDataSeries,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    LegendModifier,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    SeriesInfo,
    IRenderableSeries,
    ENumericFormat,
    XyScatterRenderableSeries,
    EllipsePointMarker,
    FastLineRenderableSeries,
    } from "scichart";
  import { TInitFunction } from "scichart-react";
  import moment from "moment";
  import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
  import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
  
  export type TTimelineConfigFunc = TInitFunction<SciChartSurface>;

  export interface Props {
    chartData: any;
  }

  export interface ChartOptions {
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesTimeline: (series: any, isChecked: boolean) => void;
    visibleSeriesTimeline: { [key: string]: boolean;}
    aggregator: string;
    isSimpleChart: boolean;
  }
  
  export const getChartAPI = (props:Props, chartOptions:ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

    const theme = GetChartTheme();  
  
    const configAfterInit = (initResult) => {
    } 
  
    const initChart : TTimelineConfigFunc = async (rootElement: string | HTMLDivElement) => {
        const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});

      // #region Setup Tooltip Data Template
      const getTooltipDataTemplate = (seriesInfo: SeriesInfo, tooltipTitle: string, tooltipLabelX: string, tooltipLabelY: string) => {
        // Lines here are returned to the tooltip and displayed as text-line per tooltip
        const lines: string[] = [];
        lines.push(tooltipTitle);
        lines.push(`x: ${seriesInfo.formattedXValue}, y: ${seriesInfo.formattedYValue}`)
    
        return lines;
    } ;
      // #endregion

      // #region Setup the XAxis
        const xAxis = new DateTimeNumericAxis(wasmContext, { 
        drawMajorBands: false, 
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
        visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
        growBy: new NumberRange(0.05, 0.05),
        labelFormat: ENumericFormat.Date_DDMMYY,
        cursorLabelFormat: ENumericFormat.Date_DDMMYY,
        labelStyle: {fontFamily:"Roboto", fontSize:12}});
  
      xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
      
      const updateSettings = (xAxis: NumericAxis) => {
        const timezone = moment.tz.guess();
        // Calculate ticks which respect the time zone
        xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
        // Draws nice axis labels timezone and locale specific
        xAxis.labelProvider = new SmartDateTZLabelProvider(timezone,true, currentLanguage?.localeId);
        const dateTzConverter = createDateTzConverter(timezone);
        // This is needed for Cursor and Rollover chart modifiers
        xAxis.labelProvider.formatCursorLabel = timestamp =>
          chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
        };
        // Setting timezone to local system
      updateSettings(xAxis);

      sciChartSurface.xAxes.add(xAxis);
  
      xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });
      
      // #endregion


      // #region Setup the Chart Modifers
      sciChartSurface.chartModifiers.add(
        new CursorModifier({ showAxisLabels: false,  id: "TimelineModifierGroup" }),
        new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
        new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
        new ZoomExtentsModifier(),
        new RolloverModifier({tooltipDataTemplate: getTooltipDataTemplate, id: "TimelineRollOver", hitTestRadius: 10}),
        new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
      );
  
      const legend = new LegendModifier({
        placementDivId: "legend-timeline",
        showCheckboxes: true,
        showSeriesMarkers: true,
        showLegend: !chartOptions.isSimpleChart,
        // Subscribe to checked changed here
        isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
          chartOptions.setVisibleSeriesTimeline(series.id, isChecked)  
        }
      });

      sciChartSurface.chartModifiers.add(legend);


      // #region Setup the Scatter Series

      // Use fixed Y-axis range to maintain consistent event type positions
      const yAxisScatter = new NumericAxis(wasmContext,{
        id: "yAxisScatter",
        axisAlignment: EAxisAlignment.Left, 
        drawMajorBands: false, 
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        isVisible: false,
        visibleRange: new NumberRange(0, props.chartData?.seriesData?.length + 1),
        growBy: new NumberRange(0.5,0.5),
        zoomExtentsToInitialRange: true, // Prevent auto-ranging on zoom extents
      });
      sciChartSurface.yAxes.add(yAxisScatter);

      // Create a series that represents the entire filter period so the chart displays the entire range.
      const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
        id: "filterPeriodSeriesId", 
        yAxisId: "yAxisScatter",
        isVisible: false,
        dataSeries: new XyDataSeries(wasmContext, {xValues: [startFilterDateUnix, endFilterDateUnix],yValues: [0,0], dataSeriesName: "filterPeriodSeries"})
      });  

      sciChartSurface.renderableSeries.add(filterPeriodSeries);
      legend.includeSeries(filterPeriodSeries, false);

      // Iterate timelineData to create series
      props.chartData?.seriesData?.forEach((seriesInfo: any) => {
        // Ensure arrays are valid and have matching lengths
        const timestamps = seriesInfo.timestamps || [];
        const yValues = seriesInfo.yAxisValueArray || [];
        
        // If arrays don't match in length, create a valid fallback
        const validTimestamps = timestamps.length === yValues.length ? timestamps : [];
        const validYValues = timestamps.length === yValues.length ? yValues : [];
        
        const scatterSeries = new XyScatterRenderableSeries(wasmContext, {
          stroke: seriesInfo.color,
          id: seriesInfo.id,
          yAxisId: "yAxisScatter",
          pointMarker: new EllipsePointMarker(wasmContext, {
            width: 12,
            height: 12,
            fill: seriesInfo.color,
            stroke: seriesInfo.color,
            strokeThickness: 2,
            opacity: 0.9,
          }),
        });

        scatterSeries.dataSeries = new XyDataSeries(wasmContext, {
          xValues: validTimestamps,
          yValues: validYValues,
          dataSeriesName: seriesInfo.name
        });

        scatterSeries.rolloverModifierProps.tooltipTextColor = 'White';
        
        // Create tooltip from tooltips array data
        scatterSeries.rolloverModifierProps.tooltipDataTemplate = (tooltipSeriesInfo: SeriesInfo) => {
          const lines: string[] = [];
          
          // Add localized series name
          const seriesName = localizationService.toLanguageString(`custom.${seriesInfo.name}`, seriesInfo.name);
          lines.push(seriesName);
          lines.push(`x: ${tooltipSeriesInfo.formattedXValue}`);

          const timestamp = tooltipSeriesInfo.xValue;
          const timestampIndex = seriesInfo.timestamps.indexOf(timestamp);
          
          if (timestampIndex !== -1 && seriesInfo.tooltips) {
            // Add tooltip data from the tooltips array
            seriesInfo.tooltips.forEach(tooltip => {
              if (tooltip.data && tooltip.data[timestampIndex] !== undefined) {
                const value = tooltip.data[timestampIndex];
                
                // Handle different data types with proper formatting
                if (typeof value === 'number' && !isNaN(value)) {
                  // Format numbers appropriately
                  const formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(2);
                  lines.push(`${tooltip.name}: ${formattedValue}`);
                } else if (typeof value === 'string') {
                  // Handle string values (like firmware versions)
                  // Show the tooltip line even for empty strings, but display "-" for empty/null values
                  const displayValue = value && value.trim() !== '' ? value : '-';
                  lines.push(`${tooltip.name}: ${displayValue}`);
                } else if (value !== null && value !== undefined) {
                  // Handle other types
                  lines.push(`${tooltip.name}: ${value}`);
                } else {
                  // Show tooltip even when value is null/undefined
                  lines.push(`${tooltip.name}: -`);
                }
              }
            });
          }
          
          return lines;
        };

        sciChartSurface.renderableSeries.add(scatterSeries);
      });


      // #endregion

      
      return { sciChartSurface: sciChartSurface};
    }

    return {initChart, configAfterInit}
}

